import { Fragment, useMemo, useState } from 'react';

import { ContactAttemptStatusBadge } from '@apps/web/components/outreaches/contact_attempt_status_badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { CallDetailDialog } from '@apps/web/components/voice_calls/call_detail_dialog';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getDeliveryModeDisplayName } from '@apps/web/lib/contact_utils';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { type components } from '@libraries/typescript/api_client';

type ClientContactAttemptRead =
    components['schemas']['ContactAttemptClientRead'];

function getAttemptTranscript(
    attempt: ClientContactAttemptRead,
): string | null {
    return (
        attempt.transcript_text?.trim() ||
        attempt.voice_call?.transcript ||
        null
    );
}

function getAttemptCallSummary(
    attempt: ClientContactAttemptRead,
): string | null {
    return (
        attempt.call_summary?.trim() || attempt.voice_call?.call_summary || null
    );
}

function getAttemptRecipientLabel(
    attempt: ClientContactAttemptRead,
): string | null {
    if (
        attempt.recipient_name &&
        attempt.recipient_name !== attempt.client_name
    ) {
        return `Contacted: ${attempt.recipient_name}`;
    }

    return null;
}

const CLIENT_NOTIFICATION_RETRYABLE_STATUSES = new Set<
    ClientContactAttemptRead['outreach_status']
>([
    'failed',
    'no_answer',
    'unreachable',
    'voicemail_left',
    'unable_to_leave_message',
    'call_disconnected',
]);
const CLIENT_NOTIFICATION_TERMINAL_STATUSES = new Set<
    ClientContactAttemptRead['outreach_status']
>(['live_answer', 'manually_notified']);

type ClientNotificationDetailModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly attempts: Array<ClientContactAttemptRead>;
    readonly timezone: string;
    readonly recordingUrls?: Record<string, string>;
    readonly onRetryAttempt: (contactAttemptId: string) => void;
    readonly onMarkNotifiedAttempt: (contactAttemptId: string) => void;
    readonly retryingContactAttemptId: string | null;
    readonly markingContactAttemptId: string | null;
    readonly onClientClick?: (clientId: string) => void;
};

function getAttemptDetailsText(attempt: ClientContactAttemptRead): string {
    const callSummary = getAttemptCallSummary(attempt);
    if (callSummary) {
        return callSummary;
    }

    const transcript = getAttemptTranscript(attempt);
    if (transcript) {
        return transcript;
    }

    return 'No transcript yet.';
}

export function ClientNotificationDetailModal({
    isOpen,
    onClose,
    attempts,
    timezone,
    recordingUrls,
    onRetryAttempt,
    onMarkNotifiedAttempt,
    retryingContactAttemptId,
    markingContactAttemptId,
    onClientClick,
}: ClientNotificationDetailModalProps) {
    const organizationId = useOrganizationId();
    const [selectedVoiceCallId, setSelectedVoiceCallId] = useState<
        string | null
    >(null);
    const groupedByClient = useMemo(() => {
        const sorted = [...attempts].sort(
            (left, right) =>
                new Date(right.created_at).getTime() -
                new Date(left.created_at).getTime(),
        );
        const groups = new Map<
            string,
            {
                readonly clientId: string;
                readonly clientName: string;
                readonly attempts: Array<ClientContactAttemptRead>;
            }
        >();
        for (const attempt of sorted) {
            const existing = groups.get(attempt.client_id);
            if (existing) {
                existing.attempts.push(attempt);
            } else {
                groups.set(attempt.client_id, {
                    clientId: attempt.client_id,
                    clientName: attempt.client_name,
                    attempts: [attempt],
                });
            }
        }
        return Array.from(groups.values());
    }, [attempts]);
    const sortedAttempts = useMemo(
        () => groupedByClient.flatMap((g) => g.attempts),
        [groupedByClient],
    );
    const selectedAttempt = useMemo(() => {
        if (!selectedVoiceCallId) {
            return null;
        }

        return (
            sortedAttempts.find(
                (attempt) => attempt.voice_call?.id === selectedVoiceCallId,
            ) ?? null
        );
    }, [selectedVoiceCallId, sortedAttempts]);

    return (
        <Fragment>
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setSelectedVoiceCallId(null);
                        onClose();
                    }
                }}
            >
                <DialogContent className="flex max-h-[70vh] min-h-[360px] flex-col overflow-hidden p-4 sm:max-w-[72vw]">
                    <DialogHeader>
                        <DialogTitle>Client Notification Attempts</DialogTitle>
                        <DialogDescription>
                            Voice attempts and outcomes for client notification
                            on this outreach.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="min-h-0 flex-1 overflow-auto">
                        {sortedAttempts.length === 0 ? (
                            <div className="text-muted-foreground py-6 text-sm">
                                No client notification attempts yet.
                            </div>
                        ) : (
                            <Table className="text-sm">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="py-2">
                                            Status
                                        </TableHead>
                                        <TableHead className="py-2">
                                            Sent
                                        </TableHead>
                                        <TableHead className="py-2">
                                            Attempt
                                        </TableHead>
                                        <TableHead className="py-2">
                                            Details
                                        </TableHead>
                                        <TableHead className="py-2">
                                            Recording
                                        </TableHead>
                                        <TableHead className="py-2">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {groupedByClient.map((group) => (
                                        <Fragment key={group.clientId}>
                                            <TableRow className="bg-muted/50">
                                                <TableCell
                                                    colSpan={6}
                                                    className="py-2 font-medium"
                                                >
                                                    {onClientClick ? (
                                                        <button
                                                            type="button"
                                                            className="text-left underline decoration-dotted underline-offset-2 hover:decoration-solid"
                                                            onClick={() =>
                                                                onClientClick(
                                                                    group.clientId,
                                                                )
                                                            }
                                                        >
                                                            {group.clientName}
                                                        </button>
                                                    ) : (
                                                        group.clientName
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {group.attempts.map((attempt) => {
                                                const maxClientContactRounds = 2;
                                                const canRetry =
                                                    CLIENT_NOTIFICATION_RETRYABLE_STATUSES.has(
                                                        attempt.outreach_status,
                                                    ) &&
                                                    attempt.round <
                                                        maxClientContactRounds -
                                                            1;
                                                const canMarkNotified =
                                                    !CLIENT_NOTIFICATION_TERMINAL_STATUSES.has(
                                                        attempt.outreach_status,
                                                    );
                                                const isRetrying =
                                                    retryingContactAttemptId ===
                                                    attempt.id;
                                                const isMarking =
                                                    markingContactAttemptId ===
                                                    attempt.id;

                                                return (
                                                    <TableRow key={attempt.id}>
                                                        <TableCell className="py-2">
                                                            <ContactAttemptStatusBadge
                                                                status={
                                                                    attempt.outreach_status
                                                                }
                                                                deliveryMode={
                                                                    attempt.delivery_mode
                                                                }
                                                                communicationDetails={
                                                                    attempt.communication_details
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell className="py-2">
                                                            <div className="text-xs">
                                                                {formatDateAndTime(
                                                                    attempt.created_at,
                                                                    timezone,
                                                                )}
                                                            </div>
                                                            <div className="text-muted-foreground text-xs">
                                                                {getDeliveryModeDisplayName(
                                                                    attempt.delivery_mode,
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="py-2 text-xs">
                                                            Round{' '}
                                                            {attempt.round + 1}
                                                        </TableCell>
                                                        <TableCell className="max-w-[380px] py-2 align-top">
                                                            <div className="space-y-1">
                                                                {getAttemptRecipientLabel(
                                                                    attempt,
                                                                ) && (
                                                                    <p className="text-xs font-medium">
                                                                        {getAttemptRecipientLabel(
                                                                            attempt,
                                                                        )}
                                                                    </p>
                                                                )}
                                                                <p className="text-muted-foreground max-h-12 overflow-auto text-xs break-words whitespace-pre-wrap">
                                                                    {getAttemptDetailsText(
                                                                        attempt,
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="py-2">
                                                            {attempt.voice_call ? (
                                                                <VoiceCallAudioPlayer
                                                                    voiceCall={
                                                                        attempt.voice_call
                                                                    }
                                                                    prefetchedRecordingUrl={
                                                                        attempt
                                                                            .voice_call
                                                                            .id
                                                                            ? recordingUrls?.[
                                                                                  attempt
                                                                                      .voice_call
                                                                                      .id
                                                                              ]
                                                                            : undefined
                                                                    }
                                                                />
                                                            ) : (
                                                                <span className="text-muted-foreground text-xs">
                                                                    —
                                                                </span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="py-2">
                                                            <div className="flex flex-wrap gap-2">
                                                                {canRetry && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            onRetryAttempt(
                                                                                attempt.id,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isRetrying ||
                                                                            isMarking
                                                                        }
                                                                    >
                                                                        {isRetrying
                                                                            ? 'Retrying…'
                                                                            : 'Retry'}
                                                                    </Button>
                                                                )}
                                                                {canMarkNotified && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            onMarkNotifiedAttempt(
                                                                                attempt.id,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isMarking ||
                                                                            isRetrying
                                                                        }
                                                                    >
                                                                        {isMarking
                                                                            ? 'Updating…'
                                                                            : 'Mark Notified'}
                                                                    </Button>
                                                                )}
                                                                {attempt
                                                                    .voice_call
                                                                    ?.id && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            setSelectedVoiceCallId(
                                                                                attempt
                                                                                    .voice_call!
                                                                                    .id,
                                                                            )
                                                                        }
                                                                    >
                                                                        View
                                                                        call
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            {organizationId && (
                <CallDetailDialog
                    organizationId={organizationId}
                    voiceCallId={selectedVoiceCallId}
                    prefetchedCall={null}
                    caregiverName={
                        selectedAttempt?.recipient_name ??
                        selectedAttempt?.client_name ??
                        null
                    }
                    open={selectedVoiceCallId !== null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setSelectedVoiceCallId(null);
                        }
                    }}
                    timezone={timezone}
                />
            )}
        </Fragment>
    );
}
